import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Search, Filter, Edit3, Trash2, X, ExternalLink, Award, CheckCircle2,
  Clock, AlertTriangle, FileText, Link2, ChevronRight, ArrowRight, Calendar,
  DollarSign, Building2, User, Mail, FileSpreadsheet
} from 'lucide-react';
import { grantsApi } from '../services/api';
import { rdProjectsApi } from '../services/rdProjectsApi';

// ─── shared style constants (match RdProjectsPage.jsx) ───────────────────────
const input = 'w-full px-3 py-2 bg-[#eef1f4]/80 border border-[#c5cbd1] rounded-lg text-sm text-[#15191d] focus:outline-none focus:ring-2 focus:ring-[#8f1024]/20 focus:border-[#8f1024] shadow-[inset_0_1px_1px_rgba(255,255,255,0.8)]';
const btnP  = 'px-4 py-2 bg-[#8f1024] text-white rounded-xl text-xs font-semibold hover:bg-[#75101f] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_18px_rgba(30,30,30,0.28)]';
const btnS  = 'px-4 py-2 bg-[#d2d6da] text-[#4c1f28] rounded-xl text-xs font-semibold hover:bg-[#c7ccd1] transition-colors inline-flex items-center gap-1.5 shadow-[0_6px_14px_rgba(20,25,30,0.18)]';
const label = 'block text-xs font-semibold text-[#1d2227] uppercase tracking-wider mb-1';
const panel = 'bg-[#dce1e5] border border-[#b9c0c7] rounded-2xl shadow-[0_10px_24px_rgba(31,41,55,0.28)]';
const statPanel = 'bg-[#cfd5da] border border-[#b6bec6] rounded-2xl shadow-[0_9px_22px_rgba(31,41,55,0.24)] border-l-4 border-l-[#8f1024]';

const fmtR    = (v, ccy = 'ZAR') => v != null ? `${ccy==='ZAR'?'R':ccy+' '}${Number(v).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day:'numeric', month:'short', year:'numeric' }) : '—';
const isoDate = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

// ─── Grant status visual language ────────────────────────────────────────────
// Status flows: Applied → Awarded → Active → Reporting → Closed
//                          ↓
//                       Rejected (terminal)
const STATUSES = ['Applied','Awarded','Active','Reporting','Closed','Rejected'];
const STATUS_COLORS = {
  Applied:   'bg-[#8f1024] text-white',
  Awarded:   'bg-[#8f1024] text-white',
  Active:    'bg-[#8f1024] text-white',
  Reporting: 'bg-[#8f1024] text-white',
  Closed:    'bg-[#565d64] text-white',
  Rejected:  'bg-[#23272b] text-white'
};

// Allowed transitions (matches backend whitelist in RdGrantsController.Transition)
const TRANSITIONS = {
  Applied:   ['Awarded', 'Rejected'],
  Awarded:   ['Active', 'Closed'],
  Active:    ['Reporting', 'Closed'],
  Reporting: ['Closed'],
  Closed:    [],
  Rejected:  []
};

const emptyGrant = {
  funderReference: '', title: '', description: '',
  funder: '', programme: '', status: 'Applied',
  totalAwarded: 0, totalReceived: 0, totalSpent: 0,
  currency: 'ZAR', exchangeRateToZAR: null,
  applicationDate: '', awardDate: '', startDate: '', endDate: '',
  grantManagerName: '', grantManagerEmail: '', notes: ''
};

export default function GrantsPage() {
  const [grants, setGrants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', funder: '' });

  const [selected, setSelected] = useState(null);     // detail (full grant with projects)
  const [modal, setModal] = useState(null);           // 'edit' | 'link' | 'transition' | 'tranche' | 'report'
  const [form, setForm] = useState(emptyGrant);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkForm, setLinkForm] = useState({ projectId: '', allocatedAmount: '', allocatedPercentage: '', notes: '' });
  const [transitionForm, setTransitionForm] = useState({ newStatus: '', notes: '' });

  // ─── V3.4: Tranche & reporting schedule state ────────────────────────────
  // Tranche = expected payment from the funder against the grant.
  // Report   = obligation report owed to the funder.
  // Both are stored as JSON arrays on the RdGrant entity (TrancheScheduleJson,
  // ReportingScheduleJson) and saved by re-PUTting the grant via grantsApi.update.
  const [trancheForm, setTrancheForm] = useState({ id: '', date: '', amount: '', status: 'Pending', receivedDate: '', notes: '' });
  const [reportForm, setReportForm] = useState({ id: '', dueDate: '', type: 'Quarterly', status: 'Pending', submittedDate: '', notes: '' });

  // ── data loading ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await grantsApi.getAll({ search: filters.search || undefined, status: filters.status || undefined, funder: filters.funder || undefined });
      const list = r.data || [];
      setGrants(list);
      // V3.5 — Auto-select the first grant when nothing is currently selected
      // so the detail panel isn't empty on first load. We only auto-select if
      // the user hasn't already picked one; subsequent reloads (after edits or
      // filter changes) preserve the user's selection if it's still in the list.
      setSelected(prev => {
        if (prev && list.some(g => g.id === prev.id)) return prev; // keep current selection
        return null; // will trigger the effect below to auto-pick the first
      });
    } catch (e) {
      console.error('Grants load failed:', e);
    }
    setLoading(false);
  }, [filters.search, filters.status, filters.funder]);
  useEffect(() => { load(); }, [load]);

  // Load projects for the link modal — we only need ref + title
  useEffect(() => {
    rdProjectsApi.getAll({}).then(r => setProjects(r.data || [])).catch(() => setProjects([]));
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await grantsApi.get(id);
      setSelected(r.data);
    } catch (e) {
      console.error('Grant detail load failed:', e);
    }
  }, []);

  // V3.5.1 — Auto-select the first grant when the list has items but nothing
  // is currently selected. Must be declared AFTER loadDetail because we
  // reference it in the dep array; declaring it earlier triggered a Temporal
  // Dead Zone "Cannot access 'loadDetail' before initialization" runtime error.
  useEffect(() => {
    if (!loading && grants.length > 0 && !selected) {
      loadDetail(grants[0].id);
    }
  }, [loading, grants, selected, loadDetail]);

  // ── derived ──
  const funders = useMemo(() => {
    const s = new Set(); grants.forEach(g => { if (g.funder) s.add(g.funder); });
    return [...s].sort();
  }, [grants]);

  const stats = useMemo(() => ({
    total:   grants.length,
    active:  grants.filter(g => g.status === 'Active').length,
    pipeline: grants.filter(g => g.status === 'Applied' || g.status === 'Awarded').length,
    totalAwarded: grants.filter(g => g.status !== 'Rejected').reduce((s, g) => s + (g.totalAwarded || 0), 0)
  }), [grants]);

  // ── handlers ──
  const openCreate = () => { setForm({ ...emptyGrant }); setEditingId(null); setModal('edit'); };
  const openEdit = (g) => {
    setForm({
      funderReference: g.funderReference || '',
      title: g.title || '',
      description: g.description || '',
      funder: g.funder || '',
      programme: g.programme || '',
      status: g.status || 'Applied',
      totalAwarded: g.totalAwarded || 0,
      totalReceived: g.totalReceived || 0,
      totalSpent: g.totalSpent || 0,
      currency: g.currency || 'ZAR',
      exchangeRateToZAR: g.exchangeRateToZAR || null,
      applicationDate: isoDate(g.applicationDate),
      awardDate: isoDate(g.awardDate),
      startDate: isoDate(g.startDate),
      endDate: isoDate(g.endDate),
      grantManagerName: g.grantManagerName || '',
      grantManagerEmail: g.grantManagerEmail || '',
      notes: g.notes || ''
    });
    setEditingId(g.id);
    setModal('edit');
  };

  const save = async () => {
    if (!form.title?.trim() || !form.funder?.trim()) {
      alert('Title and Funder are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        totalAwarded: parseFloat(form.totalAwarded) || 0,
        totalReceived: parseFloat(form.totalReceived) || 0,
        totalSpent: parseFloat(form.totalSpent) || 0,
        exchangeRateToZAR: form.exchangeRateToZAR ? parseFloat(form.exchangeRateToZAR) : null,
        applicationDate: form.applicationDate || null,
        awardDate: form.awardDate || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null
      };
      if (editingId) {
        await grantsApi.update(editingId, payload);
        if (selected?.id === editingId) loadDetail(editingId);
      } else {
        const r = await grantsApi.create(payload);
        if (r.data?.id) loadDetail(r.data.id);
      }
      setModal(null); setForm(emptyGrant); setEditingId(null);
      load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.response?.data?.title || e.message));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this grant?')) return;
    try {
      await grantsApi.delete(id);
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const transition = async () => {
    if (!transitionForm.newStatus) return;
    try {
      await grantsApi.transition(selected.id, transitionForm.newStatus, transitionForm.notes);
      setModal(null);
      setTransitionForm({ newStatus: '', notes: '' });
      loadDetail(selected.id);
      load();
    } catch (e) {
      alert('Transition failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const linkProject = async () => {
    if (!linkForm.projectId) return;
    try {
      await grantsApi.linkProject(selected.id, linkForm.projectId, {
        allocatedAmount: linkForm.allocatedAmount ? parseFloat(linkForm.allocatedAmount) : null,
        allocatedPercentage: linkForm.allocatedPercentage ? parseFloat(linkForm.allocatedPercentage) : null,
        notes: linkForm.notes || null
      });
      setModal(null);
      setLinkForm({ projectId: '', allocatedAmount: '', allocatedPercentage: '', notes: '' });
      loadDetail(selected.id);
    } catch (e) {
      alert('Link failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const unlinkProject = async (projectId) => {
    if (!window.confirm('Unlink this project from the grant?')) return;
    try {
      await grantsApi.unlinkProject(selected.id, projectId);
      loadDetail(selected.id);
    } catch (e) {
      alert('Unlink failed: ' + (e.response?.data?.error || e.message));
    }
  };

  // ─── V3.4: Tranche & reporting schedule helpers ────────────────────────────
  // Both are persisted as JSON arrays inside the grant entity. We parse on read,
  // mutate the array, then PUT the whole grant back via grantsApi.update.
  const parseSchedule = (json) => {
    if (!json) return [];
    try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; } catch { return []; }
  };

  const tranches = useMemo(() => parseSchedule(selected?.trancheScheduleJson), [selected]);
  const reports  = useMemo(() => parseSchedule(selected?.reportingScheduleJson), [selected]);

  // Derived stats for the section headers
  const trancheStats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      total:    tranches.reduce((s, t) => s + (Number(t.amount) || 0), 0),
      received: tranches.filter(t => t.status === 'Received').reduce((s, t) => s + (Number(t.amount) || 0), 0),
      overdue:  tranches.filter(t => t.status === 'Pending' && t.date && t.date < today).length
    };
  }, [tranches]);
  const reportStats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      pending:   reports.filter(r => r.status === 'Pending').length,
      submitted: reports.filter(r => r.status === 'Submitted' || r.status === 'Acknowledged').length,
      overdue:   reports.filter(r => r.status === 'Pending' && r.dueDate && r.dueDate < today).length
    };
  }, [reports]);

  // Save the entire grant with an updated schedule field
  const persistSchedule = async (field, newArray) => {
    // Build the payload the controller expects (omit the projects collection)
    const payload = {
      funderReference:        selected.funderReference,
      title:                  selected.title,
      description:            selected.description,
      funder:                 selected.funder,
      programme:              selected.programme,
      status:                 selected.status,
      totalAwarded:           selected.totalAwarded,
      totalReceived:          selected.totalReceived,
      totalSpent:             selected.totalSpent,
      currency:               selected.currency,
      exchangeRateToZAR:      selected.exchangeRateToZAR,
      applicationDate:        selected.applicationDate,
      awardDate:              selected.awardDate,
      startDate:              selected.startDate,
      endDate:                selected.endDate,
      trancheScheduleJson:    selected.trancheScheduleJson,
      reportingScheduleJson:  selected.reportingScheduleJson,
      grantManagerName:       selected.grantManagerName,
      grantManagerEmail:      selected.grantManagerEmail,
      notes:                  selected.notes
    };
    payload[field] = JSON.stringify(newArray);
    try {
      await grantsApi.update(selected.id, payload);
      loadDetail(selected.id);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.response?.data?.title || e.message));
    }
  };

  // Tranche handlers
  const openTrancheAdd = () => {
    setTrancheForm({ id: '', date: '', amount: '', status: 'Pending', receivedDate: '', notes: '' });
    setModal('tranche');
  };
  const openTrancheEdit = (t) => {
    setTrancheForm({ ...t, amount: String(t.amount ?? ''), receivedDate: t.receivedDate || '', notes: t.notes || '' });
    setModal('tranche');
  };
  const saveTranche = async () => {
    if (!trancheForm.date || !trancheForm.amount) { alert('Date and amount are required.'); return; }
    const entry = {
      id: trancheForm.id || (crypto.randomUUID ? crypto.randomUUID() : `t_${Date.now()}_${Math.random().toString(36).slice(2,8)}`),
      date: trancheForm.date,
      amount: parseFloat(trancheForm.amount) || 0,
      status: trancheForm.status,
      receivedDate: trancheForm.receivedDate || null,
      notes: trancheForm.notes || ''
    };
    const next = trancheForm.id
      ? tranches.map(t => t.id === trancheForm.id ? entry : t)
      : [...tranches, entry];
    next.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    await persistSchedule('trancheScheduleJson', next);
    setModal(null);
  };
  const deleteTranche = async (id) => {
    if (!window.confirm('Delete this tranche?')) return;
    await persistSchedule('trancheScheduleJson', tranches.filter(t => t.id !== id));
  };
  const markTrancheReceived = async (t) => {
    const today = new Date().toISOString().split('T')[0];
    const updated = { ...t, status: 'Received', receivedDate: t.receivedDate || today };
    await persistSchedule('trancheScheduleJson', tranches.map(x => x.id === t.id ? updated : x));
  };

  // Reporting handlers
  const openReportAdd = () => {
    setReportForm({ id: '', dueDate: '', type: 'Quarterly', status: 'Pending', submittedDate: '', notes: '' });
    setModal('report');
  };
  const openReportEdit = (r) => {
    setReportForm({ ...r, submittedDate: r.submittedDate || '', notes: r.notes || '' });
    setModal('report');
  };
  const saveReport = async () => {
    if (!reportForm.dueDate || !reportForm.type) { alert('Due date and report type are required.'); return; }
    const entry = {
      id: reportForm.id || (crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random().toString(36).slice(2,8)}`),
      dueDate: reportForm.dueDate,
      type: reportForm.type,
      status: reportForm.status,
      submittedDate: reportForm.submittedDate || null,
      notes: reportForm.notes || ''
    };
    const next = reportForm.id
      ? reports.map(r => r.id === reportForm.id ? entry : r)
      : [...reports, entry];
    next.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
    await persistSchedule('reportingScheduleJson', next);
    setModal(null);
  };
  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report entry?')) return;
    await persistSchedule('reportingScheduleJson', reports.filter(r => r.id !== id));
  };
  const markReportSubmitted = async (r) => {
    const today = new Date().toISOString().split('T')[0];
    const updated = { ...r, status: 'Submitted', submittedDate: r.submittedDate || today };
    await persistSchedule('reportingScheduleJson', reports.map(x => x.id === r.id ? updated : x));
  };

  // Projects available for linking (filter out already-linked ones)
  const availableProjects = useMemo(() => {
    if (!selected) return projects;
    const linkedIds = new Set((selected.projects || []).map(p => p.projectId));
    return projects.filter(p => !linkedIds.has(p.id));
  }, [projects, selected]);

  return (
    <div className="min-h-screen bg-[#e4e8ec] -m-6 px-8 py-6 space-y-6 text-[#161a1f]">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[34px] font-black leading-none text-[#161a1f] tracking-tight">Grant Management</h1>
          <p className="text-[15px] text-[#1f252a] mt-3">Register, track lifecycle, and link funder grants to one or more R&amp;D projects.</p>
        </div>
        <button onClick={openCreate} className={btnP + ' !px-5 !py-2.5'}><Plus size={14}/> New Grant</button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <div className={statPanel + " px-5 py-4 min-h-[104px]"}>
          <p className="text-[11px] font-black uppercase tracking-wider text-[#7b2531]">Total Grants</p>
          <p className="text-[34px] leading-none font-black text-[#8f1024] mt-3">{stats.total}</p>
        </div>
        <div className={statPanel + " px-5 py-4 min-h-[104px]"}>
          <p className="text-[11px] font-black uppercase tracking-wider text-[#7b2531]">Active</p>
          <p className="text-[34px] leading-none font-black text-[#8f1024] mt-3">{stats.active}</p>
        </div>
        <div className={statPanel + " px-5 py-4 min-h-[104px]"}>
          <p className="text-[11px] font-black uppercase tracking-wider text-[#7b2531]">Pipeline</p>
          <p className="text-[34px] leading-none font-black text-[#8f1024] mt-3">{stats.pipeline}</p>
          <p className="text-[12px] text-[#15191d] mt-1">Applied - Awarded</p>
        </div>
        <div className={statPanel + " px-5 py-4 min-h-[104px]"}>
          <p className="text-[11px] font-black uppercase tracking-wider text-[#7b2531]">Total Awarded</p>
          <p className="text-[30px] leading-none font-black text-[#8f1024] mt-3">{fmtR(stats.totalAwarded)}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 bg-[#eef1f4]/80 border border-[#c5cbd1] rounded-lg px-4 py-2 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9)] min-w-[320px]">
          <Search size={14} className="text-[#7b2531]"/>
          <input className="bg-transparent text-sm w-full focus:outline-none placeholder:text-[#5f6870]" placeholder="Search title, ref, funder..."
                 value={filters.search} onChange={e=>setFilters(f=>({...f, search:e.target.value}))}/>
        </div>
        <select className="w-full max-w-[180px] px-4 py-2 bg-[#8f1024] border border-[#8f1024] rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#8f1024]/20 shadow-[0_6px_14px_rgba(20,25,30,0.18)]" style={{ backgroundColor: '#8f1024', color: '#ffffff' }} value={filters.status} onChange={e=>setFilters(f=>({...f, status:e.target.value}))}>
          <option value="" style={{ backgroundColor: '#eef1f4', color: '#161a1f' }}>All Statuses</option>
          {STATUSES.map(s=><option key={s} value={s} style={{ backgroundColor: '#eef1f4', color: '#161a1f' }}>{s}</option>)}
        </select>
        <select className="w-full max-w-[180px] px-4 py-2 bg-[#8f1024] border border-[#8f1024] rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#8f1024]/20 shadow-[0_6px_14px_rgba(20,25,30,0.18)]" style={{ backgroundColor: '#8f1024', color: '#ffffff' }} value={filters.funder} onChange={e=>setFilters(f=>({...f, funder:e.target.value}))}>
          <option value="" style={{ backgroundColor: '#eef1f4', color: '#161a1f' }}>All Funders</option>
          {funders.map(f=><option key={f} value={f} style={{ backgroundColor: '#eef1f4', color: '#161a1f' }}>{f}</option>)}
        </select>
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6 items-start">
        {/* List */}
        <div className="space-y-2">
          {loading && <p className="text-center py-8 text-[#6b747d] text-sm">Loading...</p>}
          {!loading && grants.length === 0 && (
            <div className="bg-[#dce1e5] rounded-2xl border border-[#b9c0c7] p-8 text-center">
              <Award size={36} className="text-slate-300 mx-auto mb-2"/>
              <p className="text-sm text-[#4b535b] font-semibold">No grants yet</p>
              <p className="text-[11px] text-[#6b747d] mt-1">Click <b>New Grant</b> to register the first one.</p>
            </div>
          )}
          {grants.map(g => (
            <div key={g.id} onClick={()=>loadDetail(g.id)}
                 className={`rounded-2xl border p-4 cursor-pointer transition-all shadow-[0_7px_18px_rgba(31,41,55,0.20)] ${selected?.id===g.id ? 'bg-[#cfd5da] border-[#8f1024] border-l-4' : 'bg-[#dce1e5] border-[#b9c0c7] hover:bg-[#d3d9de]'}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono text-[11px] font-black text-[#7b2531] bg-[#c2c8ce] px-2 py-0.5 rounded">{g.referenceNo}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[g.status]||'bg-[#c3c9ce] text-[#3b4249]'}`}>{g.status}</span>
              </div>
              <p className="text-[16px] font-black text-[#171b20] line-clamp-2">{g.title}</p>
              <div className="flex items-center gap-3 mt-3 text-[12px] text-[#252b31] flex-wrap">
                <span className="flex items-center gap-1"><Building2 size={10}/>{g.funder}{g.programme?` · ${g.programme}`:''}</span>
                <span className="flex items-center gap-1"><DollarSign size={10}/>{fmtR(g.totalAwarded, g.currency)}</span>
                {g.projectCount > 0 && <span className="flex items-center gap-1"><Link2 size={10}/>{g.projectCount} project{g.projectCount===1?'':'s'}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className="min-w-0">
          {!selected ? (
            <div className={panel + " p-12 text-center border-dashed"}>
              <Award size={48} className="text-slate-300 mx-auto mb-3"/>
              <p className="text-sm text-[#4b535b] font-semibold">Select a grant to view details</p>
              <p className="text-[11px] text-[#6b747d] mt-1">Click a grant from the list on the left, or create a new one.</p>
            </div>
          ) : (
            <div className={panel + " p-6 space-y-6"}>
              {/* Top */}
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[11px] font-black text-[#7b2531] bg-[#c2c8ce] px-2 py-0.5 rounded">{selected.referenceNo}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[selected.status]||'bg-[#c3c9ce] text-[#3b4249]'}`}>{selected.status}</span>
                    {selected.funderReference && (
                      <span className="text-[10px] text-[#4b535b]">Funder ref: <span className="font-mono text-[#252b31]">{selected.funderReference}</span></span>
                    )}
                  </div>
                  <h2 className="text-[22px] font-black text-[#161a1f]">{selected.title}</h2>
                  {selected.description && <p className="text-[15px] text-[#161a1f] mt-2 leading-snug">{selected.description}</p>}
                </div>
                <div className="flex gap-2">
                  {TRANSITIONS[selected.status]?.length > 0 && (
                    <button onClick={()=>{ setTransitionForm({ newStatus: TRANSITIONS[selected.status][0], notes: '' }); setModal('transition'); }} className={btnP}>
                      <ArrowRight size={12}/>Advance Status
                    </button>
                  )}
                  <button onClick={()=>openEdit(selected)} className={btnS}><Edit3 size={12}/>Edit</button>
                  <button onClick={()=>remove(selected.id)} className={btnS+' !text-red-600 hover:!bg-red-50'}><Trash2 size={12}/>Delete</button>
                </div>
              </div>

              {/* Key facts */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-5 text-xs">
                <div><span className={label}>Funder</span><p className="text-sm font-semibold text-[#161a1f]">{selected.funder}</p>{selected.programme && <p className="text-[11px] text-[#4b535b]">{selected.programme}</p>}</div>
                <div><span className={label}>Total Awarded</span><p className="text-sm font-semibold text-[#161a1f]">{fmtR(selected.totalAwarded, selected.currency)}</p></div>
                <div><span className={label}>Received</span><p className="text-sm font-semibold text-emerald-700">{fmtR(selected.totalReceived, selected.currency)}</p></div>
                <div><span className={label}>Spent</span><p className="text-sm font-semibold text-[#8f1024]">{fmtR(selected.totalSpent, selected.currency)}</p></div>
                <div><span className={label}>Application Date</span><p className="text-sm text-[#252b31]">{fmtDate(selected.applicationDate)}</p></div>
                <div><span className={label}>Award Date</span><p className="text-sm text-[#252b31]">{fmtDate(selected.awardDate)}</p></div>
                <div><span className={label}>Start Date</span><p className="text-sm text-[#252b31]">{fmtDate(selected.startDate)}</p></div>
                <div><span className={label}>End Date</span><p className="text-sm text-[#252b31]">{fmtDate(selected.endDate)}</p></div>
                {selected.grantManagerName && <div><span className={label}>Grant Manager</span><p className="text-sm text-[#161a1f]">{selected.grantManagerName}</p>{selected.grantManagerEmail && <a href={`mailto:${selected.grantManagerEmail}`} className="text-[11px] text-[#c8102e] hover:underline">{selected.grantManagerEmail}</a>}</div>}
              </div>

              {/* Linked projects */}
              <div className="border-t-4 border-[#8f1024] pt-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider">Linked Projects ({(selected.projects||[]).length})</h3>
                  <button onClick={()=>setModal('link')} className={btnS+' !py-1.5 !text-[10px]'}><Plus size={12}/>Link Project</button>
                </div>
                {(selected.projects||[]).length === 0 ? (
                  <p className="text-xs text-[#6b747d] italic">No projects linked to this grant yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.projects.map(p => (
                      <div key={p.linkId} className="bg-[#cfd5da] rounded-xl p-3 flex items-center gap-3 shadow-[0_4px_10px_rgba(31,41,55,0.16)]">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] font-bold text-[#8f1024]">{p.referenceNo}</span>
                            <span className="text-sm font-semibold text-[#161a1f] truncate">{p.title}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-[#4b535b] flex-wrap">
                            <span>Status: <b className="text-[#252b31]">{p.status}</b></span>
                            {p.allocatedAmount != null && <span>Allocated: <b className="text-[#252b31]">{fmtR(p.allocatedAmount, selected.currency)}</b></span>}
                            {p.allocatedPercentage != null && <span>({p.allocatedPercentage}%)</span>}
                            {p.notes && <span className="italic">"{p.notes}"</span>}
                          </div>
                        </div>
                        <button onClick={()=>unlinkProject(p.projectId)} className="text-red-400 hover:text-red-600" title="Unlink">
                          <X size={14}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ─── V3.4: Tranche Schedule ─── */}
              <div className="border-t-4 border-[#8f1024] pt-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider">Tranche Schedule ({tranches.length})</h3>
                    <p className="text-[10px] text-[#6b747d] mt-0.5">Expected payments from the funder. Mark each tranche as <b>Received</b> when the money lands.</p>
                  </div>
                  <button onClick={openTrancheAdd} className={btnS+' !py-1.5 !text-[10px]'}><Plus size={11}/>Add Tranche</button>
                </div>

                {tranches.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3 text-[10px]">
                    <div className="bg-[#cfd5da] rounded-lg px-3 py-2"><p className="text-[#4b535b] font-semibold uppercase">Scheduled</p><p className="text-base font-black text-[#161a1f]">{fmtR(trancheStats.total, selected.currency)}</p></div>
                    <div className="bg-emerald-50 rounded-lg px-3 py-2"><p className="text-emerald-700 font-semibold uppercase">Received</p><p className="text-base font-black text-emerald-700">{fmtR(trancheStats.received, selected.currency)}</p></div>
                    <div className={`rounded-lg px-3 py-2 ${trancheStats.overdue>0?'bg-red-50':'bg-[#cfd5da]'}`}><p className={`font-semibold uppercase ${trancheStats.overdue>0?'text-red-700':'text-[#4b535b]'}`}>Overdue</p><p className={`text-base font-black ${trancheStats.overdue>0?'text-red-700':'text-[#252b31]'}`}>{trancheStats.overdue}</p></div>
                  </div>
                )}

                {tranches.length === 0 ? (
                  <p className="text-[11px] text-[#6b747d] italic py-3">No tranches scheduled yet — click <b>Add Tranche</b> to plan payments.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#cfd5da] text-[10px] text-[#4b535b] uppercase">
                          <th className="px-2 py-1.5 text-left font-semibold">Due Date</th>
                          <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                          <th className="px-2 py-1.5 text-center font-semibold">Status</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Received</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Notes</th>
                          <th className="px-2 py-1.5 text-right font-semibold w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tranches.map(t => {
                          const today = new Date().toISOString().split('T')[0];
                          const isOverdue = t.status === 'Pending' && t.date && t.date < today;
                          const statusTone = t.status === 'Received' ? 'bg-emerald-100 text-emerald-700'
                            : isOverdue ? 'bg-red-100 text-red-700'
                            : t.status === 'Overdue' ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700';
                          const effectiveStatus = isOverdue ? 'Overdue' : t.status;
                          return (
                            <tr key={t.id} className="border-t border-[#8f1024]">
                              <td className="px-2 py-1.5 text-[#252b31]">{fmtDate(t.date)}</td>
                              <td className="px-2 py-1.5 text-right font-semibold text-[#161a1f]">{fmtR(t.amount, selected.currency)}</td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusTone}`}>{effectiveStatus}</span></td>
                              <td className="px-2 py-1.5 text-[#3b4249] text-[11px]">{fmtDate(t.receivedDate)}</td>
                              <td className="px-2 py-1.5 text-[#4b535b] text-[11px] max-w-[200px] truncate" title={t.notes}>{t.notes || '—'}</td>
                              <td className="px-2 py-1.5 text-right">
                                <div className="inline-flex items-center gap-1">
                                  {t.status !== 'Received' && (
                                    <button onClick={()=>markTrancheReceived(t)} className="p-1 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700" title="Mark received">
                                      <CheckCircle2 size={12}/>
                                    </button>
                                  )}
                                  <button onClick={()=>openTrancheEdit(t)} className="p-1 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700" title="Edit"><Edit3 size={12}/></button>
                                  <button onClick={()=>deleteTranche(t.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete"><Trash2 size={12}/></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ─── V3.4: Reporting Schedule ─── */}
              <div className="border-t-4 border-[#8f1024] pt-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider">Reporting Schedule ({reports.length})</h3>
                    <p className="text-[10px] text-[#6b747d] mt-0.5">Reports owed to the funder. Track upcoming deadlines and submitted reports.</p>
                  </div>
                  <button onClick={openReportAdd} className={btnS+' !py-1.5 !text-[10px]'}><Plus size={11}/>Add Report</button>
                </div>

                {reports.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3 text-[10px]">
                    <div className="bg-blue-50 rounded-lg px-3 py-2"><p className="text-blue-700 font-semibold uppercase">Pending</p><p className="text-base font-black text-blue-700">{reportStats.pending}</p></div>
                    <div className="bg-emerald-50 rounded-lg px-3 py-2"><p className="text-emerald-700 font-semibold uppercase">Submitted</p><p className="text-base font-black text-emerald-700">{reportStats.submitted}</p></div>
                    <div className={`rounded-lg px-3 py-2 ${reportStats.overdue>0?'bg-red-50':'bg-[#cfd5da]'}`}><p className={`font-semibold uppercase ${reportStats.overdue>0?'text-red-700':'text-[#4b535b]'}`}>Overdue</p><p className={`text-base font-black ${reportStats.overdue>0?'text-red-700':'text-[#252b31]'}`}>{reportStats.overdue}</p></div>
                  </div>
                )}

                {reports.length === 0 ? (
                  <p className="text-[11px] text-[#6b747d] italic py-3">No reporting obligations recorded yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-[#cfd5da] text-[10px] text-[#4b535b] uppercase">
                          <th className="px-2 py-1.5 text-left font-semibold">Due Date</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                          <th className="px-2 py-1.5 text-center font-semibold">Status</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Submitted</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Notes</th>
                          <th className="px-2 py-1.5 text-right font-semibold w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map(r => {
                          const today = new Date().toISOString().split('T')[0];
                          const isOverdue = r.status === 'Pending' && r.dueDate && r.dueDate < today;
                          const statusTone = r.status === 'Submitted' ? 'bg-emerald-100 text-emerald-700'
                            : r.status === 'Acknowledged' ? 'bg-emerald-100 text-emerald-700'
                            : isOverdue ? 'bg-red-100 text-red-700'
                            : r.status === 'Late' ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700';
                          const effectiveStatus = isOverdue ? 'Overdue' : r.status;
                          return (
                            <tr key={r.id} className="border-t border-[#8f1024]">
                              <td className="px-2 py-1.5 text-[#252b31]">{fmtDate(r.dueDate)}</td>
                              <td className="px-2 py-1.5 text-[#252b31]">{r.type}</td>
                              <td className="px-2 py-1.5 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusTone}`}>{effectiveStatus}</span></td>
                              <td className="px-2 py-1.5 text-[#3b4249] text-[11px]">{fmtDate(r.submittedDate)}</td>
                              <td className="px-2 py-1.5 text-[#4b535b] text-[11px] max-w-[200px] truncate" title={r.notes}>{r.notes || '—'}</td>
                              <td className="px-2 py-1.5 text-right">
                                <div className="inline-flex items-center gap-1">
                                  {r.status !== 'Submitted' && r.status !== 'Acknowledged' && (
                                    <button onClick={()=>markReportSubmitted(r)} className="p-1 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700" title="Mark submitted">
                                      <CheckCircle2 size={12}/>
                                    </button>
                                  )}
                                  <button onClick={()=>openReportEdit(r)} className="p-1 rounded hover:bg-blue-50 text-blue-500 hover:text-blue-700" title="Edit"><Edit3 size={12}/></button>
                                  <button onClick={()=>deleteReport(r.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete"><Trash2 size={12}/></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {selected.notes && (
                <div className="border-t-4 border-[#8f1024] pt-5">
                  <h3 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider mb-2">Notes</h3>
                  <p className="text-sm text-[#252b31] whitespace-pre-wrap leading-snug">{selected.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Edit modal ─── */}
      {modal === 'edit' && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-[#dce1e5] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#8f1024] flex items-center justify-between sticky top-0 bg-[#dce1e5]">
              <h3 className="text-lg font-bold text-[#161a1f]">{editingId ? 'Edit Grant' : 'New Grant'}</h3>
              <button onClick={()=>{setModal(null); setForm(emptyGrant); setEditingId(null);}} className="text-[#6b747d] hover:text-[#3b4249]"><X size={20}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><label className={label}>Title *</label><input className={input} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}/></div>
                <div className="md:col-span-2"><label className={label}>Description</label><textarea className={input+' h-16'} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
                <div><label className={label}>Funder *</label><input className={input} value={form.funder} onChange={e=>setForm(f=>({...f,funder:e.target.value}))} placeholder="NRF, DSI, WRC, etc."/></div>
                <div><label className={label}>Programme</label><input className={input} value={form.programme} onChange={e=>setForm(f=>({...f,programme:e.target.value}))} placeholder="e.g. Thuthuka"/></div>
                <div><label className={label}>Funder Reference</label><input className={input} value={form.funderReference} onChange={e=>setForm(f=>({...f,funderReference:e.target.value}))} placeholder="e.g. NRF/THUTH/2026/0014"/></div>
                <div><label className={label}>Status</label>
                  <select className={input} value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    {STATUSES.map(s=><option key={s} value={s} style={{ backgroundColor: '#eef1f4', color: '#161a1f' }}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="border-t-4 border-[#8f1024] pt-5">
                <h4 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider mb-3">Financial</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className={label}>Currency</label><input className={input} value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value.toUpperCase().slice(0,10)}))}/></div>
                  <div><label className={label}>Total Awarded</label><input type="number" min="0" step="0.01" className={input} value={form.totalAwarded} onChange={e=>setForm(f=>({...f,totalAwarded:e.target.value}))}/></div>
                  <div><label className={label}>Total Received</label><input type="number" min="0" step="0.01" className={input} value={form.totalReceived} onChange={e=>setForm(f=>({...f,totalReceived:e.target.value}))}/></div>
                  <div><label className={label}>Total Spent</label><input type="number" min="0" step="0.01" className={input} value={form.totalSpent} onChange={e=>setForm(f=>({...f,totalSpent:e.target.value}))}/></div>
                </div>
              </div>

              <div className="border-t-4 border-[#8f1024] pt-5">
                <h4 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider mb-3">Key Dates</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><label className={label}>Application Date</label><input type="date" className={input} value={form.applicationDate} onChange={e=>setForm(f=>({...f,applicationDate:e.target.value}))}/></div>
                  <div><label className={label}>Award Date</label><input type="date" className={input} value={form.awardDate} onChange={e=>setForm(f=>({...f,awardDate:e.target.value}))}/></div>
                  <div><label className={label}>Start Date</label><input type="date" className={input} value={form.startDate} onChange={e=>setForm(f=>({...f,startDate:e.target.value}))}/></div>
                  <div><label className={label}>End Date</label><input type="date" className={input} value={form.endDate} onChange={e=>setForm(f=>({...f,endDate:e.target.value}))}/></div>
                </div>
              </div>

              <div className="border-t-4 border-[#8f1024] pt-5">
                <h4 className="text-xs font-semibold text-[#4b535b] uppercase tracking-wider mb-3">Grant Manager</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className={label}>Name</label><input className={input} value={form.grantManagerName} onChange={e=>setForm(f=>({...f,grantManagerName:e.target.value}))}/></div>
                  <div><label className={label}>Email</label><input type="email" className={input} value={form.grantManagerEmail} onChange={e=>setForm(f=>({...f,grantManagerEmail:e.target.value}))}/></div>
                </div>
              </div>

              <div className="border-t-4 border-[#8f1024] pt-5">
                <label className={label}>Notes</label>
                <textarea className={input+' h-20'} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#8f1024] bg-[#cfd5da] flex justify-end gap-2 sticky bottom-0">
              <button onClick={()=>{setModal(null); setForm(emptyGrant); setEditingId(null);}} className={btnS}>Cancel</button>
              <button onClick={save} disabled={saving} className={btnP}>{saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Create Grant')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Transition modal ─── */}
      {modal === 'transition' && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-[#dce1e5] rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-[#8f1024] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#161a1f]">Advance Grant Status</h3>
              <button onClick={()=>setModal(null)} className="text-[#6b747d] hover:text-[#3b4249]"><X size={20}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-xs text-[#4b535b] mb-2">Current status: <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[selected.status]}`}>{selected.status}</span></p>
                <label className={label}>Move to</label>
                <select className={input} value={transitionForm.newStatus} onChange={e=>setTransitionForm(f=>({...f, newStatus:e.target.value}))}>
                  {TRANSITIONS[selected.status]?.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Notes (optional)</label>
                <textarea className={input+' h-20'} value={transitionForm.notes} onChange={e=>setTransitionForm(f=>({...f, notes:e.target.value}))} placeholder="e.g. Funder confirmed via email 2026-05-10..."/>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#8f1024] bg-[#cfd5da] flex justify-end gap-2">
              <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
              <button onClick={transition} disabled={!transitionForm.newStatus} className={btnP}><ArrowRight size={12}/>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Link project modal ─── */}
      {modal === 'link' && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-[#dce1e5] rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-[#8f1024] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#161a1f]">Link Project to Grant</h3>
              <button onClick={()=>setModal(null)} className="text-[#6b747d] hover:text-[#3b4249]"><X size={20}/></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className={label}>Project *</label>
                <select className={input} value={linkForm.projectId} onChange={e=>setLinkForm(f=>({...f, projectId:e.target.value}))}>
                  <option value="">— Choose project —</option>
                  {availableProjects.map(p => <option key={p.id} value={p.id}>{p.reference} — {p.title}</option>)}
                </select>
                {availableProjects.length === 0 && <p className="text-[11px] text-amber-700 mt-1">All projects are already linked to this grant.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Allocated Amount</label><input type="number" min="0" step="0.01" className={input} value={linkForm.allocatedAmount} onChange={e=>setLinkForm(f=>({...f, allocatedAmount:e.target.value}))} placeholder="Optional"/></div>
                <div><label className={label}>Allocated %</label><input type="number" min="0" max="100" step="0.1" className={input} value={linkForm.allocatedPercentage} onChange={e=>setLinkForm(f=>({...f, allocatedPercentage:e.target.value}))} placeholder="Optional"/></div>
              </div>
              <div>
                <label className={label}>Notes (optional)</label>
                <input className={input} value={linkForm.notes} onChange={e=>setLinkForm(f=>({...f, notes:e.target.value}))}/>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#8f1024] bg-[#cfd5da] flex justify-end gap-2">
              <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
              <button onClick={linkProject} disabled={!linkForm.projectId} className={btnP}><Link2 size={12}/>Link</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── V3.4: Tranche modal (add / edit) ─── */}
      {modal === 'tranche' && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-[#dce1e5] rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-[#8f1024] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#161a1f]">{trancheForm.id ? 'Edit Tranche' : 'Add Tranche'}</h3>
              <button onClick={()=>setModal(null)} className="text-[#6b747d] hover:text-[#3b4249]"><X size={20}/></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Expected Date *</label>
                  <input type="date" className={input} value={trancheForm.date} onChange={e=>setTrancheForm(f=>({...f, date:e.target.value}))}/>
                </div>
                <div>
                  <label className={label}>Amount ({selected.currency || 'ZAR'}) *</label>
                  <input type="number" min="0" step="0.01" className={input} value={trancheForm.amount} onChange={e=>setTrancheForm(f=>({...f, amount:e.target.value}))}/>
                </div>
                <div>
                  <label className={label}>Status</label>
                  <select className={input} value={trancheForm.status} onChange={e=>setTrancheForm(f=>({...f, status:e.target.value}))}>
                    <option value="Pending">Pending</option>
                    <option value="Received">Received</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Received Date</label>
                  <input type="date" className={input} value={trancheForm.receivedDate || ''} onChange={e=>setTrancheForm(f=>({...f, receivedDate:e.target.value}))} disabled={trancheForm.status!=='Received'}/>
                </div>
              </div>
              <div>
                <label className={label}>Notes</label>
                <input className={input} value={trancheForm.notes} onChange={e=>setTrancheForm(f=>({...f, notes:e.target.value}))} placeholder="e.g. Conditional on Q1 progress report acceptance"/>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#8f1024] bg-[#cfd5da] flex justify-end gap-2">
              <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
              <button onClick={saveTranche} className={btnP}>{trancheForm.id ? 'Save Changes' : 'Add Tranche'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── V3.4: Reporting obligation modal (add / edit) ─── */}
      {modal === 'report' && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-[#dce1e5] rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-[#8f1024] flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#161a1f]">{reportForm.id ? 'Edit Reporting Obligation' : 'Add Reporting Obligation'}</h3>
              <button onClick={()=>setModal(null)} className="text-[#6b747d] hover:text-[#3b4249]"><X size={20}/></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Due Date *</label>
                  <input type="date" className={input} value={reportForm.dueDate} onChange={e=>setReportForm(f=>({...f, dueDate:e.target.value}))}/>
                </div>
                <div>
                  <label className={label}>Report Type *</label>
                  <select className={input} value={reportForm.type} onChange={e=>setReportForm(f=>({...f, type:e.target.value}))}>
                    <option value="Quarterly">Quarterly Progress</option>
                    <option value="Annual">Annual Progress</option>
                    <option value="Financial">Financial Statement</option>
                    <option value="Narrative">Narrative Report</option>
                    <option value="Interim">Interim Report</option>
                    <option value="Final">Final Report</option>
                    <option value="Audit">Audit Report</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Status</label>
                  <select className={input} value={reportForm.status} onChange={e=>setReportForm(f=>({...f, status:e.target.value}))}>
                    <option value="Pending">Pending</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Late">Late</option>
                    <option value="Acknowledged">Acknowledged</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Submitted Date</label>
                  <input type="date" className={input} value={reportForm.submittedDate || ''} onChange={e=>setReportForm(f=>({...f, submittedDate:e.target.value}))} disabled={reportForm.status==='Pending'}/>
                </div>
              </div>
              <div>
                <label className={label}>Notes</label>
                <input className={input} value={reportForm.notes} onChange={e=>setReportForm(f=>({...f, notes:e.target.value}))} placeholder="e.g. Covers Year 1 deliverables"/>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#8f1024] bg-[#cfd5da] flex justify-end gap-2">
              <button onClick={()=>setModal(null)} className={btnS}>Cancel</button>
              <button onClick={saveReport} className={btnP}>{reportForm.id ? 'Save Changes' : 'Add Report'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
